import React, { useEffect, useId, useState } from "react";
import { Select, MenuItem, FormControl, Typography, Box } from "@mui/material";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PropTypes from "prop-types";

// `compact` is opt-in: SchemesList/AppliedSchemeList still render the original
// 22px label-to-field gap (16px columnGap + the label's own 6px margin).
const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "compact",
})(({ compact }) => ({
  display: "flex",
  alignItems: "center",
  columnGap: compact ? "8px" : "16px",
}));

const StyledLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compact",
})(({ compact, theme }) => ({
  minWidth: "fit-content", // Adjust based on your longest label
  whiteSpace: "nowrap",
  fontWeight: 600,
  fontSize: "0.875rem",
  marginRight: compact ? 0 : "6px",
  color: theme.palette.formText?.label || theme.palette.text.primary,
}));

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== "compact",
})(({ theme, compact }) => ({
  minWidth: compact ? 120 : 200,
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    fontSize: "1rem",
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.neutral?.surface || "rgba(26, 29, 33, 1)"
        : theme.palette.background.paper,
    "& fieldset": {
      borderColor: theme.palette.divider,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.text.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiSelect-icon": {
    color: theme.palette.text.primary,
    right: 10,
  },
  "& .MuiSelect-select": {
    paddingLeft: "16px",
  },
  [theme.breakpoints.up("sm")]: {
    width: "100%",
    marginBottom: 0,
    marginRight: "16px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginBottom: "0",
  },
}));

const CustomDropdown = ({ label, options, onChange, prefilledValue, compact = false, translateLabel = true }) => {
  const [selectedValue, setSelectedValue] = useState("");
  const { t } = useTranslation();
  // translateLabel={false}: label is already resolved (rewamp callers); options still go through t()
  const labelText = translateLabel ? t(label) : label;
  const labelId = useId();
  useEffect(() => {
    setSelectedValue(prefilledValue || "");
  }, [prefilledValue]);

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
    onChange(event.target.value);
  };

  return (
    <StyledContainer role="form" aria-label={labelText} compact={compact}>
      <StyledLabel id={labelId} compact={compact}>
        {labelText}
      </StyledLabel>
      <StyledFormControl variant="outlined" compact={compact}>
        <Select
          value={selectedValue}
          onChange={handleChange}
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          placeholder={t("COMMON_SELECT")}
          inputProps={{
            "aria-label": labelText,
          }}
          size="small"
          labelId={labelId}
          MenuProps={{
            disablePortal: true,
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {t(option.value)}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
    </StyledContainer>
  );
};

CustomDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  translateLabel: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  prefilledValue: PropTypes.string,
  /** Tightens the label-to-field gap from 22px to 8px (assets filter row). */
  compact: PropTypes.bool,
};

export default CustomDropdown;
